import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, User, Settings, ArrowRight } from 'lucide-react';
import { DynamicLogo } from '../../components/DynamicLogo';
import { ThemeToggle } from '../../components/ThemeToggle';

export function Gateway() {
  return (
    <>
      <Helmet>
        <title>Bem-vindo | Portal</title>
        <meta name="description" content="Selecione seu tipo de acesso para entrar no portal." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        {/* Header com Theme Toggle */}
        <header className="absolute top-0 right-0 p-4 z-20">
          <ThemeToggle />
        </header>

        <div className="flex-1 flex items-center justify-center p-4 relative overflow-hidden">
          {/* Background Decorative Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand/5 rounded-full blur-3xl" />
          </div>

          <div className="w-full max-w-4xl relative z-10">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="flex justify-center mb-8">
                <div className="p-4 bg-card rounded-2xl border border-border shadow-lg">
                  <DynamicLogo
                    area="login"
                    alt="Logo"
                    className="h-16 w-auto"
                    fallback={<div className="h-16 w-40" />}
                  />
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Bem-vindo ao <span className="text-brand">Folio</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg mx-auto">
                Selecione abaixo o seu tipo de acesso para continuar
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-4 md:px-0">
              {/* Client Access Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Link
                  to="/area-cliente"
                  className="group relative block h-full p-8 rounded-2xl bg-card border border-border hover:border-brand/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-brand/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="relative z-10 flex flex-col h-full items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-brand flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <User className="w-8 h-8 text-white" />
                    </div>

                    <h2 className="text-2xl font-bold mb-3">Área do Cliente</h2>
                    <p className="text-muted-foreground mb-8 flex-grow">
                      Acesse orçamentos, manutenções, PMOC e documentos da sua empresa.
                    </p>

                    <div className="flex items-center gap-2 text-brand font-medium group-hover:gap-3 transition-all">
                      Acessar Portal
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              </motion.div>

              {/* Admin Access Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Link
                  to="/admin/login"
                  className="group relative block h-full p-8 rounded-2xl bg-card border border-border hover:border-brand/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 overflow-hidden"
                >
                  <div className="absolute inset-0 bg-brand/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="relative z-10 flex flex-col h-full items-center text-center">
                    <div className="w-16 h-16 rounded-2xl bg-foreground flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Settings className="w-8 h-8 text-background" />
                    </div>

                    <h2 className="text-2xl font-bold mb-3">Área Administrativa</h2>
                    <p className="text-muted-foreground mb-8 flex-grow">
                      Painel de gestão para equipe interna e administradores do sistema.
                    </p>

                    <div className="flex items-center gap-2 text-foreground font-medium group-hover:gap-3 transition-all">
                      Acessar Admin
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-muted-foreground text-sm">
                <Shield className="w-4 h-4" />
                <span>Ambiente seguro e monitorado</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
